/**
 * ProductEditModal - iter971k-Z · Turn 5
 *
 * Standalone modal that lets a user edit an existing product/service inline
 * from anywhere in the app (Invoice line items, Bill line items, Estimates,
 * etc.).  Mirrors the ContactEditModal pattern.
 *
 * On save it PUTs to `/api/products-services/{id}` and calls
 * `onSaved(updatedProduct)` so the parent can refresh its productsServices
 * cache and the current line item's matchedProduct snapshot.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Package, Briefcase, BarChart3 } from 'lucide-react';

import { API } from '../../App';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { CurrencyInput } from '../ui/currency-input';

const CATEGORIES = [
  'Consulting', 'Development', 'Design', 'Marketing', 'Maintenance',
  'Training', 'Hardware', 'Software', 'Subscription', 'Retail Product',
  'Wholesale Product', 'Other',
];

const UNITS = [
  { value: 'hour', label: 'Per Hour' },
  { value: 'day', label: 'Per Day' },
  { value: 'project', label: 'Per Project' },
  { value: 'unit', label: 'Per Unit' },
  { value: 'month', label: 'Per Month' },
  { value: 'year', label: 'Per Year' },
];

const EMPTY = {
  name: '', type: 'service', description: '', price: '', cost_price: '',
  unit: '', taxable: true, category: '', sku: '', current_stock: '',
  create_revenue_gl: false, revenue_gl_code: '',
  // iter1094 - country-aware tax defaults captured at product level.
  tax_code: '', tax_rate: 0,
};

const ProductEditModal = ({ open, onOpenChange, productId, currencySymbol = '', onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(EMPTY);
  // iter1094 - country-aware tax vocabulary (VAT rules for NG/UK/EU,
  // Sales-Tax categories for US). Loaded once when the modal opens.
  const [taxOptions, setTaxOptions] = useState([]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/tax-management/rules`);
        // /api/tax-management/rules returns { rules: [{code, name, rate, ...}] }
        const rules = (data?.rules || []).map((r) => ({
          code: r.code || r.tax_code,
          name: r.name || r.label || r.code,
          rate: r.rate ?? 0,
        }));
        setTaxOptions(rules);
      } catch {
        setTaxOptions([]);
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open || !productId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // No dedicated GET-by-id endpoint on products-services, so pull the
        // full list and pick the target (list is capped at 500 items).
        const { data } = await axios.get(`${API}/products-services`);
        const product = (data || []).find((p) => p.id === productId);
        if (cancelled) return;
        if (!product) {
          toast.error('Product not found');
          onOpenChange(false);
          return;
        }
        setFormData({
          name: product.name || '',
          type: product.type || 'service',
          description: product.description || '',
          price: product.price ?? '',
          cost_price: product.cost_price ?? '',
          unit: product.unit || '',
          taxable: product.taxable !== false,
          category: product.category || '',
          sku: product.sku || '',
          current_stock: product.current_stock ?? '',
          create_revenue_gl: Boolean(product.has_dedicated_gl),
          revenue_gl_code: product.revenue_gl_code || '',
          // iter1094 - hydrate persisted tax defaults
          tax_code: product.tax_code || '',
          tax_rate: product.tax_rate ?? 0,
        });
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load product');
        onOpenChange(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, productId, onOpenChange]);

  const handleSave = async () => {
    if (!formData.name?.trim()) {
      toast.error('Product name is required');
      return;
    }
    if (!formData.price || parseFloat(formData.price) <= 0) {
      toast.error('Enter a valid price');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        type: formData.type,
        description: formData.description || null,
        price: parseFloat(formData.price) || 0,
        cost_price: formData.cost_price === '' ? null : parseFloat(formData.cost_price),
        unit: formData.unit || null,
        taxable: Boolean(formData.taxable),
        tax_type: null,
        // iter1094 - country-aware tax defaults
        tax_code: formData.taxable ? (formData.tax_code || null) : null,
        tax_rate: formData.taxable ? (formData.tax_rate ?? null) : null,
        tax_exemption_reason: null,
        category: formData.category || null,
        sku: formData.sku || null,
        current_stock: formData.current_stock === '' ? null : parseFloat(formData.current_stock),
        create_revenue_gl: Boolean(formData.create_revenue_gl),
        revenue_gl_code: formData.revenue_gl_code || null,
      };
      await axios.put(`${API}/products-services/${productId}`, payload);
      toast.success('Product updated');
      // Return an enriched product snapshot to the caller so line-items can
      // update their matchedProduct without an extra fetch.
      onSaved?.({ id: productId, ...payload, price: payload.price });
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update product');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="product-edit-modal"
      >
        <DialogHeader>
          <DialogTitle>Edit Product / Service</DialogTitle>
          <DialogDescription>Updates apply everywhere on the platform</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {/* Type Selection */}
            <div className="flex gap-4">
              <button
                type="button"
                className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                  formData.type === 'service'
                    ? 'border-[#283F8B] bg-[#283F8B]/5'
                    : 'border-border hover:border-muted-foreground'
                }`}
                onClick={() => setFormData({ ...formData, type: 'service' })}
                data-testid="product-edit-type-service"
              >
                <Briefcase className={`w-6 h-6 mx-auto mb-2 ${formData.type === 'service' ? 'text-[#283F8B]' : 'text-muted-foreground'}`} />
                <span className={`font-medium ${formData.type === 'service' ? 'text-[#283F8B]' : 'text-muted-foreground'}`}>Service</span>
              </button>
              <button
                type="button"
                className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                  formData.type === 'product'
                    ? 'border-[#108981] bg-[#108981]/5'
                    : 'border-border hover:border-muted-foreground'
                }`}
                onClick={() => setFormData({ ...formData, type: 'product' })}
                data-testid="product-edit-type-product"
              >
                <Package className={`w-6 h-6 mx-auto mb-2 ${formData.type === 'product' ? 'text-[#108981]' : 'text-muted-foreground'}`} />
                <span className={`font-medium ${formData.type === 'product' ? 'text-[#108981]' : 'text-muted-foreground'}`}>Product</span>
              </button>
            </div>

            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="h-11"
                data-testid="product-edit-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Price *</Label>
                <CurrencyInput
                  value={formData.price}
                  onChange={(v) => setFormData({ ...formData, price: v })}
                  currencySymbol={currencySymbol}
                  placeholder="0.00"
                  className="h-11"
                  data-testid="product-edit-price"
                />
              </div>
              <div className="space-y-2">
                <Label>Unit</Label>
                <Select value={formData.unit} onValueChange={(v) => setFormData({ ...formData, unit: v })}>
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map((u) => (
                      <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cost Price</Label>
                <CurrencyInput
                  value={formData.cost_price}
                  onChange={(v) => setFormData({ ...formData, cost_price: v })}
                  currencySymbol={currencySymbol}
                  placeholder="0.00"
                  className="h-11"
                  data-testid="product-edit-cost-price"
                />
              </div>
              {formData.type === 'product' && (
                <div className="space-y-2">
                  <Label>Current Stock</Label>
                  <Input
                    type="number"
                    value={formData.current_stock}
                    onChange={(e) => setFormData({ ...formData, current_stock: e.target.value })}
                    className="h-11"
                    data-testid="product-edit-stock"
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.type === 'product' && (
              <div className="space-y-2">
                <Label>SKU</Label>
                <Input
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  className="h-11"
                  data-testid="product-edit-sku"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-muted/40 rounded-lg">
              <div>
                <p className="font-medium text-sm">Taxable</p>
                <p className="text-xs text-muted-foreground">Apply tax when invoicing</p>
              </div>
              <Switch
                checked={formData.taxable}
                onCheckedChange={(v) => setFormData({ ...formData, taxable: v })}
                data-testid="product-edit-taxable"
              />
            </div>

            {/* iter1094-product-tax-dropdown - country-aware tax code
                dropdown. Shown only when the product is taxable. The
                option list is populated from /api/tax-management/rules
                which returns VAT rules (NG/UK/EU) or ST_* Sales-Tax
                categories (US) for the business's home country. */}
            {formData.taxable && taxOptions.length > 0 && (
              <div className="space-y-2">
                <Label>Default Tax Code</Label>
                <Select
                  value={formData.tax_code || 'NONE'}
                  onValueChange={(value) => {
                    if (value === 'NONE') {
                      setFormData({ ...formData, tax_code: '', tax_rate: 0 });
                      return;
                    }
                    const opt = taxOptions.find((o) => o.code === value);
                    setFormData({
                      ...formData,
                      tax_code: value,
                      tax_rate: opt?.rate ?? 0,
                    });
                  }}
                >
                  <SelectTrigger className="h-11" data-testid="product-edit-tax-code">
                    <SelectValue placeholder="Select default tax rate">
                      {formData.tax_code
                        ? `${taxOptions.find((o) => o.code === formData.tax_code)?.name || formData.tax_code} · ${formData.tax_rate}%`
                        : 'No default tax'}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="NONE">No default tax</SelectItem>
                    {taxOptions.map((opt) => (
                      <SelectItem key={opt.code} value={opt.code}>
                        <span className="flex items-center justify-between gap-3 w-full">
                          <span className="truncate">{opt.name}</span>
                          <span className="text-muted-foreground text-xs shrink-0">{opt.rate}%</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  This rate will auto-select when adding this item to an invoice.
                </p>
              </div>
            )}

            <div className="flex items-center justify-between p-3 bg-gradient-to-r from-[#283F8B]/5 to-[#108981]/5 rounded-lg border border-[#283F8B]/15">
              <div>
                <p className="font-medium text-sm flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-[#283F8B]" />
                  Dedicated Revenue GL Account
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Track revenue for this {formData.type} separately.
                  {formData.revenue_gl_code && (
                    <> Currently: <span className="font-mono">{formData.revenue_gl_code}</span></>
                  )}
                </p>
              </div>
              <Switch
                checked={formData.create_revenue_gl}
                onCheckedChange={(v) => setFormData({ ...formData, create_revenue_gl: v })}
                data-testid="product-edit-create-gl"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || loading} data-testid="product-edit-save">
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
            ) : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProductEditModal;
